import React, { useEffect, useMemo, useState } from 'react';
import { ButtonV2 } from '@asigna/kit';
import classNames from 'classnames';
import { isEmpty } from 'lodash';

import { useCountTxFee } from '../../../hooks/use-count-tx-fee';
import useSafe from '../../../hooks/use-safe';
import InfoIcon from '../../../images/icon_info.svg';
import { InputField } from '../../InputField/InputField';
import { SkeletonGetTxFee } from '../../Skeletons/SkeletonAsset';

import { useSetTxFeeStyles } from './useSetTxFeeStyles';

type Props = {
  transactionHex: string;
  onSend: (fee: number) => Promise<void>;
  onBackClick: () => void;
  textContent?: {
    title: string;
    backButton: string;
    actionButton: string;
  };
};

type FeeRateType = { title: string; time: string; amount: number };

enum Tabs {
  Recomended = 'Recomended',
  Custom = 'Custom',
}

enum FeeRateTitle {
  Low,
  Standard,
  High,
}

// TODO_R: [FRS]
// TODO_R: move into SafeDetailsPage/components/
export const SetTxFee = ({ transactionHex, onSend, onBackClick, textContent }: Props) => {
  const [selectedFeeRateIndex, setSelectedFeeRateIndex] = useState(1);
  const [customFee, setCustomFee] = useState('');
  const [selectedTab, setSelectedTab] = useState(Tabs.Recomended);
  const [estimations, setEstimations] = useState<number[]>();
  const [stxPrice, setStxPrice] = useState();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const classes = useSetTxFeeStyles();
  const { stxBalance } = useSafe();
  const { countTxGas } = useCountTxFee();

  // const isEnoughStxBalanceForFee = useCallback(
  //   (fee: number) => {
  //     const transaction = deserializeTransaction(transactionHex);
  //     if ('amount' in transaction.payload && transaction.payload.amount) {
  //       // TODO_R: move 'Number(transaction.payload.amount) / Math.pow(10, 6)' into properly named variable
  //       return (
  //         +stxBalance.available - Number(transaction.payload.amount) / Math.pow(10, 6) - fee > 0
  //       );
  //     }
  //
  //     return +stxBalance.available - fee > 0;
  //   },
  //   [transactionHex, stxBalance.available]
  // );

  useEffect(() => {
    if (selectedTab === Tabs.Recomended) {
      setError('');
      setCustomFee('');
    }
  }, [selectedTab]);

  useEffect(
    function loadData() {
      if (!transactionHex) return;

      const loadFees = async () => {
        const gas = await countTxGas(transactionHex);
        gas &&
          setEstimations(
            gas?.estimates.map((estimate) => Number(estimate.fee.amount) / Math.pow(10, 6))
          );
      };

      const fetchStxPrice = async () => {
        // TODO_R: reuse getPrice and check SafeDetailsPage component for the api usage
        try {
          const response = await fetch(
            'https://sapi.coincarp.com/api/v1/his/coin/trend?code=blockstack&type=d'
          );
          const data = await response.json();
          setStxPrice(data.data[0][1]);
        } catch (error) {
          console.error('Error fetching STX price:', error);
        }
      };
      setLoading(true);
      loadFees().then(() => setLoading(false));
      fetchStxPrice();
    },
    [transactionHex]
  );

  const findClosestFeeRate = (feeRates: FeeRateType[], customFee: number) => {
    return feeRates.reduce((closest, current) => {
      const closestDifference = Math.abs(closest.amount - customFee);
      const currentDifference = Math.abs(current.amount - customFee);

      return currentDifference < closestDifference ? current : closest;
    });
  };

  const feeRates: FeeRateType[] | null = useMemo(() => {
    if (!estimations || !transactionHex) return null;

    return [
      {
        title: 'Low',
        time: '~1 hour+',
        amount: +estimations[FeeRateTitle.Low],
      },
      {
        title: 'Standard',
        time: '~30 min',
        amount: +estimations[FeeRateTitle.Standard],
      },
      {
        title: 'High',
        time: '~10-20 min',
        amount: +estimations[FeeRateTitle.High],
      },
    ];
  }, [estimations, transactionHex]);

  useEffect(
    function watchForError() {
      if (!feeRates) return;
      // if (!isEnoughStxBalanceForFee(feeRates[selectedFeeRateIndex].amount)) {
      //   setError('Insufficient balance');
      // } else {
      setError('');
      // }
    },
    [selectedFeeRateIndex, feeRates]
  );

  const handleCustomFeeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value.replace(/[^0-9.]+/g, '');
    setCustomFee(inputValue);
    // if (!isEnoughStxBalanceForFee(+inputValue)) return setError('Insufficient balance');
    if (inputValue) return setError('');
  };

  const areFirstAndLastCharactersDigits = (str: string) => {
    const firstCharIsDigit = !isNaN(parseInt(str.charAt(0)));
    const lastCharIsDigit = !isNaN(parseInt(str.charAt(str.length - 1)));

    return firstCharIsDigit && lastCharIsDigit;
  };

  const isSendDisabled =
    loading ||
    !isEmpty(error) ||
    (selectedTab === Tabs.Custom && +customFee === 0) ||
    customFee ||
    (selectedTab !== Tabs.Custom && !estimations)
      ? !areFirstAndLastCharactersDigits(customFee)
      : false;

  const handleTrySend = async () => {
    if (isSendDisabled) {
      // TODO: show some notification instead of silent non-action

      return;
    }
    onSend(
      Math.round(
        selectedTab === Tabs.Custom
          ? +customFee * Math.pow(10, 6)
          : +estimations![selectedFeeRateIndex] * Math.pow(10, 6)
      )
    );
  };

  if (!transactionHex) return <></>;

  return (
    <div className={classes.wrapper}>
      <div className={classes.choseFeeNav}>
        {Object.values(Tabs).map((tab) => (
          <span
            key={tab}
            className={classNames(classes.navItem, tab === selectedTab ? classes.navItemAvive : '')}
            onClick={() => setSelectedTab(tab)}
          >
            {tab}
          </span>
        ))}
      </div>
      {loading ? (
        <SkeletonGetTxFee />
      ) : (
        <>
          {selectedTab === Tabs.Recomended && (
            <div className={classes.recomendedWrapper}>
              {feeRates?.map((feeRate, index) => (
                <div
                  key={feeRate.title}
                  className={classNames(
                    classes.feeItem,
                    selectedFeeRateIndex === index ? classes.feeItemActive : ''
                  )}
                  onClick={() => setSelectedFeeRateIndex(index)}
                >
                  <div className={classes.feeContent}>
                    <span className={classes.feeTitle}>{feeRate.title}</span>
                    <span className={classes.feeTime}>{feeRate.time}</span>
                  </div>
                  <span className={classes.feeAmount}>
                    {feeRate.amount.toFixed(6)} STX · ~$
                    {(feeRate.amount * (stxPrice ? +stxPrice : 0)).toFixed(4)}
                  </span>
                </div>
              ))}
              {error && <span className={classes.error}>{error}</span>}
            </div>
          )}

          {selectedTab === Tabs.Custom && (
            <div className={classes.customWrapper}>
              <div className={classes.hintWrapper}>
                <img src={InfoIcon} alt="" className="" />
                <span className={classes.hintContent}>
                  Higher fee rates typically lead to faster confirmation time.{' '}
                </span>
              </div>
              <div className={classes.inputWrapper}>
                <InputField
                  value={customFee}
                  label=""
                  placeHolder="STX"
                  balance={stxBalance.available.toString()}
                  stxPrice={stxPrice && +customFee > 0 ? +customFee * +stxPrice : null}
                  onChange={handleCustomFeeChange}
                />
              </div>
              <div className={classes.infoWrapper}>
                {error && <span className={classes.error}>{error}</span>}
                <div className={classes.infoRow}>
                  <span className={classes.infoRowTitle}>Estimated confirmation time</span>
                  <span className={classes.infoRowContent}>
                    {customFee && feeRates && findClosestFeeRate(feeRates, +customFee).time}
                  </span>
                </div>
              </div>
            </div>
          )}
        </>
      )}
      <div className={classes.buttonsWrapper}>
        <ButtonV2
          message="Back"
          onClick={() => onBackClick()}
          disabled={loading}
          color="secondary"
          className="w-full"
        />
        <ButtonV2
          onClick={handleTrySend}
          disabled={isSendDisabled}
          message={textContent?.actionButton || 'Send'}
          className="w-full text-white"
        />
      </div>
    </div>
  );
};
